<!-- TransferModal.vue -->
<template>
  <div class="modal fade" id="transferModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="mdi mdi-bank-transfer text-primary me-2"></i>
            Transfer Funds
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <form @submit.prevent="processTransfer">
          <div class="modal-body">
            <!-- Source Wallet Information -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card bg-light">
                  <div class="card-header">
                    <h6 class="mb-0">
                      <i class="mdi mdi-wallet text-success me-2"></i>
                      Source Wallet
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-6">
                        <div class="mb-2">
                          <strong>{{ sourceWallet.holderName }}</strong>
                        </div>
                        <div class="text-muted">{{ sourceWallet.walletId }}</div>
                        <div class="text-muted">{{ sourceWallet.phoneNumber }}</div>
                      </div>
                      <div class="col-md-6 text-end">
                        <div class="mb-2">
                          <strong>Available Balance</strong>
                        </div>
                        <div class="h5 text-success">{{ formatPrice(sourceWallet.availableBalance) }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Transfer Type Selection -->
            <div class="row mb-4">
              <div class="col-12">
                <label class="form-label fw-semibold">Transfer Type</label>
                <div class="row g-3">
                  <div class="col-md-4" v-for="type in transferTypes" :key="type.value">
                    <div class="card border cursor-pointer transfer-type-card"
                         :class="{'border-primary bg-light': transferForm.type === type.value}"
                         @click="selectTransferType(type.value)">
                      <div class="card-body text-center p-3">
                        <i :class="type.icon + ' font-size-24 mb-2'"
                           :style="{color: transferForm.type === type.value ? '#0d6efd' : '#6c757d'}"></i>
                        <h6 class="mb-1">{{ type.label }}</h6>
                        <small class="text-muted">{{ type.description }}</small>
                        <div class="mt-2">
                          <small class="badge bg-secondary">{{ type.fee }}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Destination Details -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Destination Details</h6>
              </div>
            </div>

            <!-- Wallet Transfer -->
            <div v-if="transferForm.type === 'WALLET'" class="row g-3 mb-3">
              <div class="col-md-8">
                <label class="form-label">Destination Wallet ID / Phone Number *</label>
                <input type="text" class="form-control" v-model="transferForm.destinationId"
                       @blur="validateDestination()"
                       placeholder="Enter wallet ID or phone number" required>
                <div v-if="destinationWallet" class="mt-2 p-2 bg-light rounded">
                  <small class="text-muted">Destination:</small>
                  <div class="fw-semibold">{{ destinationWallet.holderName }}</div>
                  <div class="text-muted">{{ destinationWallet.walletId }}</div>
                </div>
              </div>
              <div class="col-md-4">
                <label class="form-label">Transfer Speed</label>
                <select class="form-select" v-model="transferForm.speed" @change="updateFees()">
                  <option value="STANDARD">Standard (Free)</option>
                  <option value="EXPRESS">Express (+KES 50)</option>
                  <option value="INSTANT">Instant (+KES 100)</option>
                </select>
              </div>
            </div>

            <!-- Bank Transfer -->
            <div v-if="transferForm.type === 'BANK'" class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Bank *</label>
                <select class="form-select" v-model="transferForm.bank" required>
                  <option value="">Select Bank</option>
                  <option v-for="bank in banks" :key="bank.code" :value="bank">
                    {{ bank.name }}
                  </option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Account Number *</label>
                <input type="text" class="form-control" v-model="transferForm.accountNumber"
                       placeholder="Enter account number" required>
              </div>
              <div class="col-12">
                <label class="form-label">Account Holder Name *</label>
                <input type="text" class="form-control" v-model="transferForm.accountHolderName"
                       placeholder="Enter account holder name" required>
              </div>
            </div>

            <!-- Mobile Money Transfer -->
            <div v-if="transferForm.type === 'MOBILE'" class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Mobile Money Provider *</label>
                <select class="form-select" v-model="transferForm.mobileProvider" required>
                  <option value="">Select Provider</option>
                  <option value="MPESA">M-Pesa</option>
                  <option value="AIRTEL">Airtel Money</option>
                  <option value="TKASH">T-Kash</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Phone Number *</label>
                <div class="input-group">
                  <span class="input-group-text">+254</span>
                  <input type="tel" class="form-control" v-model="transferForm.mobileNumber"
                         placeholder="712345678" required pattern="[0-9]{9}">
                </div>
              </div>
            </div>

            <!-- Amount and Description -->
            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Transfer Amount *</label>
                <div class="input-group">
                  <span class="input-group-text">KES</span>
                  <input type="number" class="form-control" v-model="transferForm.amount"
                         @input="calculateFees()" placeholder="0.00" min="1" step="0.01" required>
                </div>
                <small class="text-muted">
                  Available: {{ formatPrice(sourceWallet.availableBalance) }}
                </small>
              </div>
              <div class="col-md-6">
                <label class="form-label">Reference/Description</label>
                <input type="text" class="form-control" v-model="transferForm.description"
                       placeholder="Enter description or reference">
              </div>
            </div>

            <!-- Transaction Summary -->
            <div class="row mb-3">
              <div class="col-12">
                <div class="card border">
                  <div class="card-header">
                    <h6 class="mb-0">Transaction Summary</h6>
                  </div>
                  <div class="card-body">
                    <table class="table table-borderless mb-0">
                      <tr>
                        <td>Transfer Amount:</td>
                        <td class="text-end">{{ formatPrice(transferForm.amount || 0) }}</td>
                      </tr>
                      <tr>
                        <td>Transaction Fee:</td>
                        <td class="text-end">{{ formatPrice(calculatedFees.transaction) }}</td>
                      </tr>
                      <tr v-if="calculatedFees.speed > 0">
                        <td>Speed Fee:</td>
                        <td class="text-end">{{ formatPrice(calculatedFees.speed) }}</td>
                      </tr>
                      <tr v-if="calculatedFees.bank > 0">
                        <td>Bank Processing Fee:</td>
                        <td class="text-end">{{ formatPrice(calculatedFees.bank) }}</td>
                      </tr>
                      <tr class="table-active">
                        <td><strong>Total Deduction:</strong></td>
                        <td class="text-end"><strong>{{ formatPrice(totalDeduction) }}</strong></td>
                      </tr>
                      <tr>
                        <td><strong>Remaining Balance:</strong></td>
                        <td class="text-end">
                          <strong :class="remainingBalance >= 0 ? 'text-success' : 'text-danger'">
                            {{ formatPrice(remainingBalance) }}
                          </strong>
                        </td>
                      </tr>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <!-- Security Verification -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Security Verification</h6>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Transaction PIN *</label>
                <input type="password" class="form-control" v-model="transferForm.pin"
                       placeholder="Enter your 4-digit PIN" maxlength="4" required>
              </div>
              <div class="col-md-6">
                <label class="form-label">Two-Factor Authentication</label>
                <div class="input-group">
                  <input type="text" class="form-control" v-model="transferForm.otp"
                         placeholder="Enter OTP" maxlength="6">
                  <button type="button" class="btn btn-outline-secondary" @click="sendOTP()"
                          :disabled="otpSent || otpCooldown > 0">
                    {{ otpCooldown > 0 ? `Wait ${otpCooldown}s` : (otpSent ? 'OTP Sent' : 'Send OTP') }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Confirmation -->
            <div class="row">
              <div class="col-12">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="transferForm.confirmed"
                         id="confirmTransfer" required>
                  <label class="form-check-label" for="confirmTransfer">
                    I confirm that all details are correct and authorize this transfer *
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <div class="d-flex justify-content-between w-100">
              <div class="text-muted">
                <small>
                  <i class="mdi mdi-shield-check text-success me-1"></i>
                  Secure Transfer • End-to-End Encrypted
                </small>
              </div>
              <div>
                <button type="button" class="btn btn-secondary me-2" data-bs-dismiss="modal">Cancel</button>
                <button type="submit" class="btn btn-primary"
                        :disabled="loading || !canTransfer()">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  <i v-else class="mdi mdi-send me-2"></i>
                  {{ loading ? 'Processing...' : 'Transfer Now' }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */

export default {
  name: "TransferModal",
  props: {
    sourceWallet: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      otpSent: false,
      otpCooldown: 0,
      destinationWallet: null,

      transferForm: {
        type: 'WALLET',
        destinationId: '',
        bank: '',
        accountNumber: '',
        accountHolderName: '',
        mobileProvider: '',
        mobileNumber: '',
        amount: '',
        description: '',
        speed: 'STANDARD',
        pin: '',
        otp: '',
        confirmed: false
      },

      transferTypes: [
        {
          value: 'WALLET',
          label: 'Wallet Transfer',
          description: 'To another e-wallet',
          icon: 'mdi mdi-wallet-transfer',
          fee: 'Free'
        },
        {
          value: 'BANK',
          label: 'Bank Transfer',
          description: 'To bank account',
          icon: 'mdi mdi-bank',
          fee: 'KES 25'
        },
        {
          value: 'MOBILE',
          label: 'Mobile Money',
          description: 'To mobile money',
          icon: 'mdi mdi-cellphone',
          fee: 'KES 10'
        }
      ],

      banks: [
        { code: 'KCB', name: 'KCB Bank Kenya' },
        { code: 'EQUITY', name: 'Equity Bank' },
        { code: 'COOP', name: 'Co-operative Bank' },
        { code: 'ABSA', name: 'Absa Bank Kenya' },
        { code: 'STANBIC', name: 'Stanbic Bank' },
        { code: 'STANDARD', name: 'Standard Chartered' },
        { code: 'DTB', name: 'Diamond Trust Bank' },
        { code: 'I&M', name: 'I&M Bank' }
      ],

      calculatedFees: {
        transaction: 0,
        speed: 0,
        bank: 0
      }
    }
  },
  computed: {
    totalDeduction() {
      const amount = parseFloat(this.transferForm.amount) || 0;
      const fees = this.calculatedFees.transaction + this.calculatedFees.speed + this.calculatedFees.bank;
      return amount + fees;
    },

    remainingBalance() {
      return this.sourceWallet.availableBalance - this.totalDeduction;
    }
  },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value);
    },

    selectTransferType(type) {
      this.transferForm.type = type;
      this.calculateFees();
    },

    calculateFees() {
      const amount = parseFloat(this.transferForm.amount) || 0;

      // Reset fees
      this.calculatedFees = {
        transaction: 0,
        speed: 0,
        bank: 0
      };

      if (amount <= 0) return;

      // Transaction fees based on type
      switch (this.transferForm.type) {
        case 'WALLET':
          this.calculatedFees.transaction = 0; // Free wallet to wallet
          break;
        case 'BANK':
          this.calculatedFees.transaction = 25; // Flat fee for bank transfers
          this.calculatedFees.bank = amount * 0.001; // 0.1% processing fee
          break;
        case 'MOBILE':
          this.calculatedFees.transaction = 10; // Flat fee for mobile money
          break;
      }

      // Speed fees for wallet transfers
      if (this.transferForm.type === 'WALLET') {
        switch (this.transferForm.speed) {
          case 'EXPRESS':
            this.calculatedFees.speed = 50;
            break;
          case 'INSTANT':
            this.calculatedFees.speed = 100;
            break;
        }
      }
    },

    updateFees() {
      this.calculateFees();
    },

    async validateDestination() {
      if (this.transferForm.type === 'WALLET' && this.transferForm.destinationId) {
        // Simulate API call to validate destination
        try {
          // Mock destination wallet data
          const mockWallets = [
            {
              walletId: 'KE001234567891',
              holderName: 'Jane Doe Wanjiku',
              phoneNumber: '+254722345679'
            },
            {
              walletId: 'KE001234567892',
              holderName: 'Peter Mwangi Kimani',
              phoneNumber: '+254733456780'
            }
          ];

          const found = mockWallets.find(w =>
              w.walletId === this.transferForm.destinationId ||
              w.phoneNumber === '+254' + this.transferForm.destinationId ||
              w.phoneNumber === this.transferForm.destinationId
          );

          this.destinationWallet = found || null;
        } catch (error) {
          console.error('Error validating destination:', error);
          this.destinationWallet = null;
        }
      }
    },

    canTransfer() {
      const hasAmount = this.transferForm.amount && this.transferForm.amount > 0;
      const hasPin = this.transferForm.pin && this.transferForm.pin.length === 4;
      const hasConfirmed = this.transferForm.confirmed;
      const hasSufficientBalance = this.remainingBalance >= 0;

      let hasDestination = false;
      switch (this.transferForm.type) {
        case 'WALLET':
          hasDestination = this.transferForm.destinationId && this.destinationWallet;
          break;
        case 'BANK':
          hasDestination = this.transferForm.bank && this.transferForm.accountNumber && this.transferForm.accountHolderName;
          break;
        case 'MOBILE':
          hasDestination = this.transferForm.mobileProvider && this.transferForm.mobileNumber;
          break;
      }

      return hasAmount && hasPin && hasConfirmed && hasSufficientBalance && hasDestination;
    },

    async sendOTP() {
      try {
        this.otpSent = true;
        this.otpCooldown = 60;

        // Start countdown
        const interval = setInterval(() => {
          this.otpCooldown--;
          if (this.otpCooldown <= 0) {
            clearInterval(interval);
            this.otpSent = false;
          }
        }, 1000);

        // Simulate OTP sending
        console.log('OTP sent to:', this.sourceWallet.phoneNumber);
      } catch (error) {
        console.error('Error sending OTP:', error);
        this.otpSent = false;
        this.otpCooldown = 0;
      }
    },

    generateTransactionRef() {
      const timestamp = Date.now().toString();
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      return 'TXN' + timestamp.slice(-6) + random;
    },

    async processTransfer() {
      try {
        this.loading = true;

        // Simulate API call
        const transferData = {
          id: this.generateTransactionRef(),
          sourceWalletId: this.sourceWallet.walletId,
          destinationId: this.transferForm.destinationId,
          destinationName: this.getDestinationName(),
          type: this.transferForm.type,
          amount: parseFloat(this.transferForm.amount),
          fees: this.calculatedFees.transaction + this.calculatedFees.speed + this.calculatedFees.bank,
          totalDeduction: this.totalDeduction,
          description: this.transferForm.description || 'Fund Transfer',
          status: 'COMPLETED',
          timestamp: new Date().toISOString(),
          reference: this.generateTransactionRef()
        };

        // Simulate processing delay
        await new Promise(resolve => setTimeout(resolve, 3000));

        this.$emit('transfer-completed', transferData);

        // Reset form
        this.resetForm();

        // Close modal
        const modal = bootstrap.Modal.getInstance(document.getElementById('transferModal'));
        modal.hide();

      } catch (error) {
        console.error('Error processing transfer:', error);
      } finally {
        this.loading = false;
      }
    },

    getDestinationName() {
      switch (this.transferForm.type) {
        case 'WALLET':
          return this.destinationWallet?.holderName || 'Unknown Wallet';
        case 'BANK':
          return this.transferForm.accountHolderName;
        case 'MOBILE':
          return '+254' + this.transferForm.mobileNumber;
        default:
          return 'Unknown Destination';
      }
    },

    resetForm() {
      this.transferForm = {
        type: 'WALLET',
        destinationId: '',
        bank: '',
        accountNumber: '',
        accountHolderName: '',
        mobileProvider: '',
        mobileNumber: '',
        amount: '',
        description: '',
        speed: 'STANDARD',
        pin: '',
        otp: '',
        confirmed: false
      };
      this.destinationWallet = null;
      this.otpSent = false;
      this.otpCooldown = 0;
      this.calculatedFees = {
        transaction: 0,
        speed: 0,
        bank: 0
      };
    }
  },
  watch: {
    'transferForm.amount'() {
      this.calculateFees();
    }
  }
}
</script>

<style scoped>
.transfer-type-card {
  cursor: pointer;
  transition: all 0.3s ease;
}

.transfer-type-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.cursor-pointer {
  cursor: pointer;
}

.table td {
  padding: 0.5rem;
}

.modal-dialog {
  max-width: 800px;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.input-group-text {
  background-color: #e9ecef;
  border-color: #ced4da;
}
</style>